import React, { useEffect, useState } from "react";
import { Control, FieldErrors, FieldValues } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import { RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCServicesApi, RCCTextArea, RCCTooltip } from "react-common-components";
import Attachments from "src/components/Attachments";

import Title from "src/components/Title";
import { standardErrorMessages } from "src/utils/errors";
import { convertIFileDTOArrayToFileArray } from "src/Interfaces/fileDTO";
import formatDate from "src/utils/formatDate";
import SelectAuditSituation from "../selectSituation";
import { IAuditItems } from "../../interface";
import BadgeAuditSituation from "../badgeSituation";
import { cardStyle } from "../../styles";

export interface IItemAudited{
	fetchAttachments: boolean;
    item: IAuditItems;
	index: number;
	disabled: boolean;
    control: Control<FieldValues>;
	register: (RegisterOptions?: any) => void;
    errors: FieldErrors<FieldValues>;
	setValue?: ((name: string, value: unknown, config?: Object | undefined) => void) | undefined
}

const ItemAudited: React.FC<IItemAudited> = ({
	fetchAttachments,
	item,
	index,
	disabled,
	control,
	register,
	errors,
	setValue
}) => {
	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();

	const [showComment, setShowComment] = useState<boolean>(false);
	const [showAttachments, setShowAttachments] = useState<boolean>(false);

	const [attachments, setAttachments] = useState<File[] | undefined>();

	const handleFetchAttachments = async () => {
		try{
			const response = await RCCServicesApi.getRequest({
				method: "Audits/SelectAttachments",
				urlParameters: {
					id: item.adb004Id
				}
			});
			convertFile(response.data);
			
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar anexos"), addToast);
		}
	};

	const convertFile = (fileDto: any[]) => {
		let dataFormatted: File[] = [];

		if(fileDto.length > 0){
			dataFormatted = convertIFileDTOArrayToFileArray(fileDto);
		}

		setAttachments(dataFormatted);
	};

	useEffect(() => {
		if(!fetchAttachments){
			convertFile(item.files);
		}
	},[item]);

	return (
		<RCCGrid 
			container
			item
			styleInline={cardStyle}
		>
			<RCCGrid
				container
				item
				justifyContent="space-between"
			>
				<Title color="purple" size="sm">
					{`${translate("Item da Auditoria")}: ${item.adb004Codigo}`}
				</Title>

				{
					item.dateModified && item.adb004Situacao &&
					<Title color="purple">
						{`${translate("Data de alteração")}: `} 
						{formatDate(item.dateModified, currentLanguage)}
						<RCCIcon name="calendar" style={{ marginLeft: "0.2rem"}}/>
					</Title>
				}
				
			</RCCGrid>

			<RCCGrid
				container
				item
				alignContent="space-between"
			>
				<input 
					name={`adtb004[${index}].adb004Id`} 
					type="text"
					value={item.adb004Id || 0}
					{...register(`adtbb004[${index}].adb004Id`)} 
					style={{ display: "none" }} 
					readOnly
				/>

				<input 
					name={`adtb004[${index}].adb004Codigo`} 
					type="text"
					value={item.adb004Codigo}
					{...register(`adtbb004[${index}].adb004Codigo`)} 
					style={{ display: "none" }} 
					readOnly
				/>

				<input 
					name={`adtb004[${index}].adb004Descricao`} 
					type="text"
					value={item.adb004Descricao}
					{...register(`adtbb004[${index}].adb004Descricao`)} 
					style={{ display: "none" }} 
					readOnly
				/>

				<RCCGrid
					container
					item
					md={6}
					xs={12}
					styleInline="display: block; align-self: center;"
				>
					<div style={{marginLeft: 4}}>
						<span>{item.adb004Descricao}</span>
					</div>
				</RCCGrid>
				<RCCGrid
					container
					item
					md={4}
					xs={12}
				>
					{
						disabled 
							? 
							<BadgeAuditSituation 
								value={item.adb004Situacao}
							/>
							:
							<SelectAuditSituation 
								name={`adtbb004[${index}].adb004Situacao`} 
								control={control}
								defaultValue={item.adb004Situacao}
							/>
					}					
				</RCCGrid>

				<RCCGrid
					container
					item
					alignContent="flex-end"
					justifyContent="flex-end"
					md={2}
					xs={12}
				>
					<RCCTooltip
						text={translate("Comentário")}
						color={showComment || item.adb004Comentario ? "blue" : "grey"}
						position="bottom"
					>
						<RCCButton
							layout="isRounded"
							icon={{
								name: "comment"
							}}
							color={showComment || item.adb004Comentario ? "blue" : "grey"}
							onClick={() => setShowComment((prevState) => !prevState)}
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Anexos")}
						color={showAttachments || item.adtbb005.length > 0  ? "blue" : "grey"}
						position="bottom"
					>
						{
							fetchAttachments ? 
								<>
									<RCCButton
										layout="isRounded"
										icon={{
											name: "attachment"
										}}
										color={showAttachments || item.adtbb005.length > 0 ? "blue" : "grey"}
										onClick={async (e) => {
											e.stopPropagation();
											if(!attachments){
												await handleFetchAttachments();
											}
											setShowAttachments((prevState) => !prevState);
										}}
									/>
								</>

								:

								<>
									<RCCButton
										layout="isRounded"
										icon={{
											name: "attachment"
										}}
										color={showAttachments || item.adtbb005.length > 0 ? "blue" : "grey"}
										onClick={() => {setShowAttachments((prevState) => !prevState);}}
									/>
								</>
							
						}
						
					</RCCTooltip>
					{
						item.adb004Ocb001Id &&
						<RCCTooltip
							text={translate("Ocorrência")}
							color="blue"
							position="bottom"
						>
							<RCCButton
								layout="isRounded"
								icon={{
									name: "linkExternal"
								}}
								color="blue"
								onClick={() => window.open(`/occurrences/panel/${item.adb004Ocb001Id}`)}
							/>
						</RCCTooltip>
					}
				</RCCGrid>
			</RCCGrid>

			<RCCGrid 
				container 
				item
				xs={12}
				style={showComment ? {} : { display: "none" }}
			>
				<RCCTextArea 
					register={register}
					label={translate("Comentário")}
					name={`adtbb004[${index}].adb004Comentario`}
					error={errors.adb004Comentario}
					defaultValue={item.adb004Comentario}
					disabled={disabled}
					maxLength={4000}
				/>
			</RCCGrid>
			{
				attachments &&
				<RCCGrid 
					container 
					item
					xs={12}
					style={showAttachments ? {} : { display: "none" }}
					styleInline="margin-top: 15px !important"
				>
					<Attachments 
						name={`adtbb004[${index}].attachments`}
						filesInit={attachments}
						setValue={setValue}
						disabled={disabled}
						styleInline="width: 100%;"
					/>
				</RCCGrid>
			}

		</RCCGrid>
	);
};


export default ItemAudited;
